"use client";

import { useState, memo, useCallback, useEffect } from "react";
import Image from "next/image";

import { Card, CardBody, CardFooter, Tooltip } from "@heroui/react";
import { FaInstagram, FaYoutube, FaDiscord, FaLinkedin } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { MdEmail } from "react-icons/md";
import { IconType } from "react-icons";
import { motion } from "framer-motion";

import { Social, SocialPlatform } from "@/lib/types";
import { teamMembers } from "@/app/data/teamMembers";
import DefaultPfp from "@/../public/assets/profilePictures/default.png";
import BrowserDetector from "@/lib/supportedBrowser";

export const SOCIAL_ICONS: Record<SocialPlatform, IconType> = {
    [SocialPlatform.twitter]: FaXTwitter,
    [SocialPlatform.instagram]: FaInstagram,
    [SocialPlatform.youtube]: FaYoutube,
    [SocialPlatform.discord]: FaDiscord,
    [SocialPlatform.linkedin]: FaLinkedin,
    [SocialPlatform.email]: MdEmail,
};

type TeamMemberFooterProps = { socials?: Social[] };

const listVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.15 } },
};

const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

export default function OurTeam() {
    const [flipped, setFlipped] = useState<boolean[]>(teamMembers.map(() => false));
    const [isSupported, setIsSupported] = useState<boolean>(false)

    const toggleFlip = useCallback((index: number) => {
        setFlipped(prev => prev.map((v, i) => (i === index ? !v : v)));
    }, []);

    useEffect(() => {
        const browserDetector = new BrowserDetector()
        if (browserDetector.isSupported()) {
            setIsSupported(true)
        }
    }, [])

    return (
        <motion.div variants={listVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.div variants={cardVariants}>
                <h2 className="second-header">MEET THE TEAM</h2>
            </motion.div>

            <div className="flex flex-wrap justify-center py-10 gap-4">
                {teamMembers.map((member, index) => (
                    <div key={member.name} className={isSupported ? `transition-all duration-200 ease-out hover:-translate-y-1 hover:shadow-xl cursor-pointer` : ""}>
                        <motion.div
                            className="relative w-48 h-72"
                            onClick={() => isSupported && toggleFlip(index)}
                            animate={{ rotateY: flipped[index] ? 180 : 0 }}
                            transition={{ duration: 0.6, ease: "easeInOut" }}
                            style={{ transformStyle: "preserve-3d" }}
                            variants={cardVariants}
                        >
                            {/* FRONT */}
                            <Card
                                radius="lg"
                                className="absolute inset-0 backface-hidden bg-black/30 backdrop-blur-xl outline-2 outline-black purple-glow"
                                style={{ transform: "rotateY(0deg)" }}
                            >
                                <CardBody className="flex flex-col items-center text-white gap-4">
                                    <p className="text-lg font-semibold">{member.name}</p>
                                    <div className="h-48 w-full overflow-hidden">
                                        <Image
                                            src={member.profilePicture ?? DefaultPfp}
                                            alt={`Profile: ${member.name}`}
                                            className="w-full h-full object-contain"
                                            width={200}
                                            height={200}
                                        />
                                    </div>
                                    <p className="text-sm">{member.title}</p>
                                </CardBody>
                                <CardFooter>
                                    <MemoizedTeamMemberFooter socials={member.socials} />
                                </CardFooter>
                            </Card>

                            {/* BACK */}
                            {isSupported && (
                                <Card
                                    radius="lg"
                                    className="absolute inset-0 backface-hidden bg-black/30 backdrop-blur-xl outline-2 outline-black purple-glow"
                                    style={{ transform: "rotateY(180deg)" }}
                                >
                                    <CardBody>
                                        <div className="flex items-center justify-center h-full relative">
                                            <div className="absolute top-6 text-center w-full">
                                                <p className="font-semibold text-white">Favourite Game</p>
                                                <p className="text-primary-100">{member.gameOfChoice}</p>
                                            </div>
                                            <div className="text-center pt-6 text-white">{member.bio}</div>
                                        </div>
                                    </CardBody>
                                </Card>
                            )}
                        </motion.div>
                    </div>
                ))}
            </div>
        </motion.div>
    );
}

const TeamMemberFooter = ({ socials }: TeamMemberFooterProps) => {
    const [copiedClicked, setCopiedClicked] = useState(false);

    if (!socials || socials.length === 0) return null;

    const handleDiscordClick = (link: string, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        navigator.clipboard.writeText(link).then(() => {
            setCopiedClicked(true);
            setTimeout(() => setCopiedClicked(false), 1500);
        });
    };

    return (
        <CardFooter className="flex gap-4 justify-around">
            {socials.map(({ social, link }) => {
                const Icon = SOCIAL_ICONS[social];
                if (social === SocialPlatform.discord) {
                    return (
                        <button
                            key={social}
                            onClick={(e) => handleDiscordClick(link, e)}
                            className="text-white hover:text-primary-100 hover:scale-125 transition-colors ease-in-out transform cursor-pointer"
                            aria-label={social}
                        >
                            <Tooltip
                                key={social}
                                content="Username Copied!"
                                isOpen={copiedClicked}
                                placement="top"
                                showArrow
                                delay={0}
                                closeDelay={1500}
                                color="secondary"
                                motionProps={{ transition: { duration: 0.3, ease: "easeInOut" } }}
                            >
                                <Icon size={24} />
                            </Tooltip>
                        </button>
                    );
                }
                return (
                    <a
                        className="text-white hover:text-primary-100 hover:scale-125 transition-colors ease-in-out transform"
                        key={social}
                        href={link}
                        onClick={(e) => e.stopPropagation()}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={social}
                    >
                        <Icon size={24} />
                    </a>
                );
            })}
        </CardFooter>
    );
};

const MemoizedTeamMemberFooter = memo(TeamMemberFooter);